import { useSnackbar } from 'notistack';
import { GibType } from 'library/types';
import apiService from 'library/api-service';
import { FormattedMessage } from 'react-intl';
import { GridRowId } from '@mui/x-data-grid-premium';
import { ChangeEvent, FormEvent, useCallback, useEffect, useState } from 'react';
import { Box, Button, TextField, DialogTitle, DialogContentText } from '@mui/material';
import { LoadingBackdrop, ResponsiveDialog, ResponsiveDialogActions, ResponsiveDialogContent } from 'components';

type UpdateGibTypeDialogProps = {
  open: boolean;
  gibTypeId: GridRowId;
  handleClose: () => void;
  refresh: () => Promise<void>;
};

const UpdateGibTypeDialog = ({ open, gibTypeId, handleClose: closeDialog, refresh }: UpdateGibTypeDialogProps) => {
  // other hooks
  const { enqueueSnackbar } = useSnackbar();

  // useState hooks
  const [title, setTitle] = useState('');
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  // async helper functions
  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!title) {
      setError(true);
      return;
    }

    const postData: GibType = {
      id: gibTypeId as number,
      title,
    };

    try {
      setLoading(true);

      const response = await apiService.updateGibType(postData);

      if (response.isSuccess) {
        enqueueSnackbar(
          <FormattedMessage
            id="gibTypes.updateGibTypeCompleted"
            defaultMessage="GİB Yöntemi düzenleme işlemi başarıyla tamamlandı."
          />,
          { variant: 'success' }
        );
        handleClose();
        refresh();
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const getGibTypeInfos = useCallback(async () => {
    try {
      setLoading(true);

      const response = await apiService.getSpecificGibType(gibTypeId as number);

      if (response.isSuccess) {
        setTitle(response.data.title);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  }, [gibTypeId]);

  // sync helper functions
  const handleClose = () => {
    closeDialog();
    setTitle('');
    setError(false);
  };

  const onChangeHandler = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setTitle(event.target.value);

  // useEffect hooks
  useEffect(() => {
    if (title) {
      setError(false);
    }
  }, [title]);

  useEffect(() => {
    if (open && gibTypeId) {
      getGibTypeInfos();
    }
  }, [open, gibTypeId, getGibTypeInfos]);

  return (
    <ResponsiveDialog maxWidth="sm" open={open} onClose={handleClose}>
      <DialogTitle>
        <FormattedMessage id="gibTypes.updateGibType" defaultMessage="GİB Yöntemi Düzenle" />
      </DialogTitle>
      <Box component="form" onSubmit={onSubmit} noValidate>
        <ResponsiveDialogContent>
          <DialogContentText>
            <FormattedMessage
              id="gibTypes.updateGibTypeText"
              defaultMessage="İlgili alanları değiştirip devam ederek seçili GİB Yöntemi için düzenleme yapabilirsiniz."
            />
          </DialogContentText>
          <TextField
            label={<FormattedMessage id="gibTypes.title" defaultMessage="Adı" />}
            fullWidth
            size="small"
            onChange={onChangeHandler}
            value={title}
            error={error}
            helperText={
              error && <FormattedMessage id="validations.required" defaultMessage="Bu alan boş bırakılamaz" />
            }
          />
        </ResponsiveDialogContent>
        <ResponsiveDialogActions>
          <Button onClick={handleClose}>
            <FormattedMessage id="forgotPassword.cancel" defaultMessage="Vazgeç" />
          </Button>
          <Button variant="contained" type="submit">
            <FormattedMessage id="forgotPassword.continue" defaultMessage="Devam" />
          </Button>
        </ResponsiveDialogActions>
      </Box>
      <LoadingBackdrop open={loading} />
    </ResponsiveDialog>
  );
};

export default UpdateGibTypeDialog;
